import { gpx as parseGpx } from '@tmcw/togeojson';

const TRACK_COLORS = [
	'#ff0000',
	'#E63946',
	'#2A9D8F',
	'#F4A261',
	'#457B9D',
	'#8338EC',
	'#FB5607',
	'#06D6A0',
];

export const gpxMethods = {

	async _loadGPX( gpxUrl ) {
		/* global mapboxgl */
		const chartWrap = document.getElementById( 'elevation-chart-wrap' );
		const s         = window.lbhMapStrings || {};
		console.log( 'LBHMap: fetching GPX from', gpxUrl );
		try {
			const res = await fetch( gpxUrl, { credentials: 'same-origin' } );

			if ( ! res.ok ) {
				console.error( `LBHMap: GPX fetch failed — HTTP ${ res.status } for ${ gpxUrl }` );
				chartWrap.innerHTML = `<p class="chart-error">${ s.gpxUnavailable || 'Tracciato GPX non disponibile.' }</p>`;
				return;
			}

			const text = await res.text();
			const xml  = new DOMParser().parseFromString( text, 'application/xml' );

			const parseErr = xml.querySelector( 'parsererror' );
			if ( parseErr ) {
				console.error( 'LBHMap: GPX parse error', parseErr.textContent );
				chartWrap.innerHTML = `<p class="chart-error">${ s.gpxInvalid || 'Formato GPX non valido.' }</p>`;
				return;
			}

			const geojson = parseGpx( xml );

			if ( ! geojson.features.length ) {
				console.warn( 'LBHMap: GPX parsed but contains no features', gpxUrl );
				chartWrap.innerHTML = '';
				return;
			}

			this._removeGPXLayer();

			this.map.addSource( 'gpx-track', { type: 'geojson', data: geojson } );
			this.map.addLayer( {
				id:     'gpx-track-line',
				type:   'line',
				source: 'gpx-track',
				layout: { 'line-join': 'round', 'line-cap': 'round' },
				paint:  { 'line-color': '#062423', 'line-width': 3 },
			} );

			const coords = geojson.features
				.filter( f => f.geometry && f.geometry.type === 'LineString' )
				.flatMap( f => f.geometry.coordinates );

			if ( coords.length ) {
				const bounds = coords.reduce(
					( b, c ) => b.extend( c ),
					new mapboxgl.LngLatBounds( coords[ 0 ], coords[ 0 ] )
				);
				this.map.fitBounds( bounds, { padding: 60, maxZoom: 13 } );
			}

			const elevations = coords.map( c => c[ 2 ] ).filter( e => e != null && ! isNaN( e ) );
			this._drawElevationChart( elevations, chartWrap );

		} catch ( err ) {
			console.error( 'LBHMap: GPX load exception', err );
			chartWrap.innerHTML = `<p class="chart-error">${ s.gpxUnavailable || 'Tracciato GPX non disponibile.' }</p>`;
		}
	},

	_removeGPXLayer() {
		if ( this.map.getLayer( 'gpx-track-line' ) ) this.map.removeLayer( 'gpx-track-line' );
		if ( this.map.getSource( 'gpx-track' ) )    this.map.removeSource( 'gpx-track' );
	},

	// ── Multiple GPX tracks (hotel detail) ─────────────────────────────────────

	async _loadAllNearbyGPX( nearby ) {
		this._clearNearbyGPXLayers();
		const session  = ++this._nearbyGPXSession;
		const withGpx  = nearby.filter( iti => iti.gpx_url );
		if ( ! withGpx.length ) return;
		await Promise.allSettled( withGpx.map( iti => this._loadNearbyGPXTrack( iti, session ) ) );
	},

	async _loadNearbyGPXTrack( iti, session ) {
		const sourceId = `gpx-nearby-${ iti.id }`;
		const layerId  = `gpx-nearby-${ iti.id }-line`;
		try {
			const res = await fetch( iti.gpx_url, { credentials: 'same-origin' } );
			if ( ! res.ok || this._nearbyGPXSession !== session ) return;

			const text = await res.text();
			if ( this._nearbyGPXSession !== session ) return;

			const xml = new DOMParser().parseFromString( text, 'application/xml' );
			if ( xml.querySelector( 'parsererror' ) || this._nearbyGPXSession !== session ) return;

			const geojson = parseGpx( xml );
			if ( ! geojson.features.length || this._nearbyGPXSession !== session ) return;

			if ( this.map.getSource( sourceId ) || this.map.getLayer( layerId ) ) return;

			this.map.addSource( sourceId, { type: 'geojson', data: geojson } );
			this.map.addLayer( {
				id:     layerId,
				type:   'line',
				source: sourceId,
				layout: { 'line-join': 'round', 'line-cap': 'round' },
				paint:  { 'line-color': '#062423', 'line-width': 2.5, 'line-opacity': 0.7 },
			} );
			this._nearbyGPXLayerIds.push( { sourceId, layerId, itiId: iti.id } );

			// Draw elevation chart inside the card
			const coords = geojson.features
				.filter( f => f.geometry && f.geometry.type === 'LineString' )
				.flatMap( f => f.geometry.coordinates );
			const elevations = coords.map( c => c[ 2 ] ).filter( e => e != null && ! isNaN( e ) );
			const chartEl = document.getElementById( `iti-chart-${ iti.id }` );
			if ( chartEl && elevations.length ) {
				this._drawElevationChart( elevations, chartEl );
			}
		} catch ( err ) {
			console.error( `LBHMap: GPX load error for iti ${ iti.id }`, err );
		}
	},

	_clearNearbyGPXLayers() {
		this._nearbyGPXLayerIds.forEach( ( { sourceId, layerId } ) => {
			if ( this.map.getLayer( layerId ) )   this.map.removeLayer( layerId );
			if ( this.map.getSource( sourceId ) ) this.map.removeSource( sourceId );
		} );
		this._nearbyGPXLayerIds = [];
	},

	_drawElevationChart( elevations, container ) {
		if ( ! elevations.length ) {
			container.innerHTML = '';
			return;
		}

		const W    = 300;
		const H    = 80;
		const minE = Math.min( ...elevations );
		const maxE = Math.max( ...elevations );
		const rng  = maxE - minE || 1;

		const step    = Math.max( 1, Math.floor( elevations.length / 250 ) );
		const sampled = elevations.filter( ( _, i ) => i % step === 0 );

		const pts = sampled.map( ( e, i ) => {
			const x = ( i / ( sampled.length - 1 ) ) * W;
			const y = H - ( ( e - minE ) / rng ) * ( H - 4 );
			return `${ x.toFixed( 1 ) },${ y.toFixed( 1 ) }`;
		} ).join( ' ' );

		const s = window.lbhMapStrings || {};
		container.innerHTML = `
			<h4 class="chart-title">${ s.chartTitle || 'Altimetria' }</h4>
			<div class="chart-labels">
				<span>${ Math.round( maxE ) } m</span>
				<span>${ Math.round( minE ) } m</span>
			</div>
			<svg class="elevation-svg" viewBox="0 0 ${ W } ${ H }" preserveAspectRatio="none"
				xmlns="http://www.w3.org/2000/svg">
				<polyline points="${ pts }" fill="none" stroke="#062423"
					stroke-width="2" stroke-linejoin="round"/>
			</svg>`;
	},
};
